import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  bufferTime,
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil
} from 'rxjs'
import { getOppositeDirection } from '../../components/DepthOfMarket/helpers'
import { getHub } from '../../helpers/hub'
import { getNextMessageId } from '../helpers'
import { getSelectedUser } from '../users/selectors'
import { addRfqWindow } from '../windows/actions'
import { logError } from '../ws/actions'
import { getServerTimeDelayMs } from '../ws/selectors'
import {
  AcceptRfqQuoteAction,
  acceptRfqQuoteFailure,
  acceptRfqQuoteSuccess,
  CancelRfqAction,
  cancelRfqFailure,
  cancelRfqSuccess,
  fetchRFQActivitySuccess,
  FetchRFQsAction,
  fetchRFQsSuccess,
  RejectRfqQuoteAction,
  rejectRfqQuoteFailure,
  rejectRfqQuoteSuccess,
  SubmitRfqAction,
  submitRFQFailure
} from './actions'
import {
  rfQEntryModelToServerQuoteRequest,
  rfqQuoteRequestResponseToRfqQuoteRequest
} from './helpers'
import { getActiveRfqQuoteRequestForSecurity } from './selectors'
import { QuoteRequest, RawQuoteRequest } from './types'

const BUFFER_DELAY = 300

const fetchRFQsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<FetchRFQsAction>('rfq.fetchRFQs'),
    switchMap((action: FetchRFQsAction) => {
      const userId = getSelectedUser(state$.value)
      const serverDelayMs = getServerTimeDelayMs(state$.value)
      return getHub()
        .stream('GetRFQs', userId, action.payload.showAll)
        .pipe(
          bufferTime(BUFFER_DELAY),
          map((arr) => arr.flat()),
          filter((arr) => arr.length > 0),
          map((arr: RawQuoteRequest[]) =>
            arr
              .map((r) =>
                rfqQuoteRequestResponseToRfqQuoteRequest(r, serverDelayMs)
              )
              .filter((val) => val !== '#ERR#')
          ),
          mergeMap((response) => {
            return of(
              fetchRFQsSuccess(response),
              fetchRFQActivitySuccess(response)
            )
          }),
          takeUntil(action$.pipe(ofType('rfq.fetchRFQUnsubscribe'))),
          catchError((err) => of(logError(err)))
        )
    })
  )

const submitRFQEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('rfq.submitRfq'),
    mergeMap((action: SubmitRfqAction) => {
      const { order, rfq, transactionId, custId } = action.payload
      const size = Number(rfq.size)
      const price = rfq.price ? Number(rfq.price) : undefined
      const messageId = getNextMessageId()
      const selUser = getSelectedUser(state$.value)
      const tempRfqRequest: QuoteRequest = {
        securityId: order.securityId,
        isSpread: rfq.isSpread,
        type: getOppositeDirection(order.type),
        size,
        price,
        quotes: [],
        bestResponse: undefined,
        isLive: true,
        isExpired: false,
        exposureDuration: rfq.exposureDuration,
        hedge: rfq.hedge,
        isASAP: rfq.isASAP,
        discloseMyName: rfq.discloseMyName
      } as const
      return getHub()
        .invoke<number>(
          'CreateRFQ',
          selUser,
          rfQEntryModelToServerQuoteRequest(rfq),
          custId,
          messageId
        )
        .pipe(
          mergeMap(() =>
            of(
              fetchRFQsSuccess([tempRfqRequest]),
              addRfqWindow({
                orderId: order.id,
                size,
                type: order.type,
                securityId: order.securityId
              })
            )
          ),
          catchError((err) =>
            of(
              submitRFQFailure(
                order.securityId,
                transactionId,
                order.type,
                err
              ),
              logError(err)
            )
          )
        )
    })
  )

const cancelRfqRequestEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<CancelRfqAction>('rfq.cancelRfq'),
    switchMap((action) => {
      const securityId = action.payload.securityId
      const quoteRequest = getActiveRfqQuoteRequestForSecurity(state$.value)(
        securityId
      )
      if (
        quoteRequest?.requestId &&
        !quoteRequest.quotes.some((quote) => !quote.isLive)
      ) {
        return getHub()
          .invoke<number>('CancelRFQ', quoteRequest.requestId)
          .pipe(
            mergeMap(() => of(cancelRfqSuccess(securityId))),
            catchError((err) =>
              of(
                cancelRfqFailure(
                  securityId,
                  getOppositeDirection(quoteRequest.type),
                  err
                ),
                logError(err)
              )
            )
          )
      }
      return of(cancelRfqSuccess(securityId))
    })
  )

const acceptRfqQuoteEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<AcceptRfqQuoteAction>('rfq.acceptRfqQuote'),
    switchMap((action) => {
      const { securityId, requestId, quoteId } = action.payload
      const quoteRequest = getActiveRfqQuoteRequestForSecurity(state$.value)(
        securityId
      )
      // should not happen, but will cause a runtime error if somehow it does
      if (!quoteRequest) return of(EMPTY)
      const quote = quoteRequest.quotes.find((q) => q.id === quoteId)
      if (!quote) {
        return of(
          acceptRfqQuoteFailure(
            securityId,
            quoteRequest.type,
            'Quote not found.'
          )
        )
      }
      return getHub()
        .invoke<number>(
          'HitLiftRFQ',
          requestId,
          quoteId,
          quote.price,
          quoteRequest.size
        )
        .pipe(
          mergeMap(() => of(acceptRfqQuoteSuccess(securityId, quoteId))),
          catchError((err) =>
            of(
              acceptRfqQuoteFailure(securityId, quoteRequest.type, err.message),
              logError(err)
            )
          )
        )
    })
  )
const rejectRfqQuoteEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType<RejectRfqQuoteAction>('rfq.rejectRfqQuote'),
    switchMap((action) => {
      return getHub()
        .invoke('PassRFQ', action.payload.requestId)
        .pipe(
          mergeMap(() =>
            of(
              rejectRfqQuoteSuccess(
                action.payload.securityId,
                action.payload.requestId
              )
            )
          ),
          catchError((err) => {
            const securityId = action.payload.securityId
            const quoteRequest = getActiveRfqQuoteRequestForSecurity(
              state$.value
            )(securityId)
            // this would happen if the user cancelled after rejecting
            if (!quoteRequest) return of(EMPTY)
            return of(
              rejectRfqQuoteFailure(securityId, quoteRequest.type, err.message),
              logError(err)
            )
          })
        )
    })
  )

export default combineEpics(
  acceptRfqQuoteEpic,
  cancelRfqRequestEpic,
  rejectRfqQuoteEpic,
  submitRFQEpic,
  fetchRFQsEpic
)
